
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import userAuthService from '../services/userAuthService';

// Define guest patient type
export type GuestPatient = {
  fullName: string;
  email: string;
  phone: string;
  gender?: string;
  age?: number;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  notes?: string;
};

// Define context type for guest operations
type UserAuthContextType = {
  guestInfo: GuestPatient | null;
  isGuest: boolean;
  saveGuestInfo: (patientData: GuestPatient) => void;
  clearGuestInfo: () => void;
  updateGuestInfo: (updates: Partial<GuestPatient>) => void;
  isLoading: boolean;
  
  // Legacy methods (stubs for compatibility)
  isAuthenticated: boolean;
  user: any;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: any) => Promise<boolean>;
  updateProfile: (updates: any) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
};

// Create context
const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

// Create provider component
export const UserAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [guestInfo, setGuestInfo] = useState<GuestPatient | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { toast } = useToast();
  
  // Check if guest info is already saved in localStorage
  useEffect(() => {
    const checkGuestInfo = () => {
      const storedInfo = userAuthService.getGuestInfo();
      
      if (storedInfo) {
        setGuestInfo(storedInfo);
      }
      
      setIsLoading(false);
    };
    
    checkGuestInfo();
  }, []);
  
  // Save guest patient info
  const saveGuestInfo = (patientData: GuestPatient) => {
    try {
      const savedData = userAuthService.storeGuestInfo(patientData);
      setGuestInfo(savedData);
      
      toast({
        title: 'Information Saved',
        description: 'Your patient information has been saved for this session.',
      });
    } catch (error: any) {
      toast({
        title: 'Error Saving Information',
        description: 'There was a problem saving your information.',
        variant: 'destructive',
      });
    }
  };
  
  // Clear guest patient info
  const clearGuestInfo = () => {
    userAuthService.clearGuestInfo();
    setGuestInfo(null);
    
    toast({
      title: 'Information Cleared',
      description: 'Your patient information has been cleared.',
    });
  };
  
  // Update guest patient info
  const updateGuestInfo = (updates: Partial<GuestPatient>) => {
    if (!guestInfo) {
      toast({
        title: 'Error',
        description: 'No guest information to update.',
        variant: 'destructive',
      });
      return;
    }
    
    const updatedInfo = {
      ...guestInfo,
      ...updates
    };
    
    userAuthService.storeGuestInfo(updatedInfo);
    setGuestInfo(updatedInfo);
    
    toast({
      title: 'Information Updated',
      description: 'Your patient information has been updated.',
    });
  };
  
  // Stub methods for compatibility
  const login = async () => {
    toast({
      title: 'Login Unavailable',
      description: 'Patient accounts functionality is not available.',
      variant: 'destructive',
    });
    return false;
  };
  
  const logout = () => {
    clearGuestInfo();
  };
  
  const register = async () => {
    toast({
      title: 'Registration Unavailable',
      description: 'Patient accounts functionality is not available.',
      variant: 'destructive',
    });
    return false;
  };
  
  const updateProfile = async () => {
    toast({
      title: 'Profile Update Unavailable',
      description: 'Patient profile functionality is not available.',
      variant: 'destructive',
    });
    return false;
  };
  
  const changePassword = async () => {
    toast({
      title: 'Password Change Unavailable',
      description: 'Patient account functionality is not available.',
      variant: 'destructive',
    });
    return false;
  };
  
  const requestPasswordReset = async () => {
    toast({
      title: 'Password Reset Unavailable',
      description: 'Patient account functionality is not available.',
      variant: 'destructive',
    });
    return false;
  };
  
  const resetPassword = async () => {
    toast({
      title: 'Password Reset Unavailable',
      description: 'Patient account functionality is not available.',
      variant: 'destructive',
    });
    return false;
  };
  
  return (
    <UserAuthContext.Provider value={{ 
      guestInfo,
      isGuest: guestInfo !== null,
      saveGuestInfo,
      clearGuestInfo,
      updateGuestInfo,
      isLoading,
      // Stub properties and methods
      user: null,
      isAuthenticated: false,
      login,
      logout,
      register,
      updateProfile,
      changePassword,
      requestPasswordReset,
      resetPassword
    }}>
      {children}
    </UserAuthContext.Provider>
  );
};

// Create hook for using the auth context
export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};
